import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tooltip,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { padding, textAlign } from "@mui/system";
import { ChevronLeft, ChevronLeftIcon } from "lucide-react";
import axiosMain from "../http/axiosMain";
import AddCoinsModal from "./AddCoinsModal ";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { useNavigate } from "react-router-dom";
import { GlobalContext } from "../../utils/GlobalContext";

const HeadTextSx = {
  fontFamily: "Poppins",
  fontWeight: 500,
  fontSize: "24px",
  lineHeight: "120%",
  letterSpacing: "0%",
  verticalAlign: "middle",
  color: "rgba(101, 101, 101, 1)",
};

const TitleTextSx = {
  fontFamily: "Poppins",
  fontWeight: 600,
  fontSize: "20px",
  lineHeight: "140%",
  letterSpacing: "0%",
  verticalAlign: "middle",
  color: "rgba(18, 18, 18, 1)",
};

const FaqTitleSx = {
  color: "rgba(29, 27, 32, 1)",
  fontFamily: "Poppins",
  fontWeight: 500,
  fontSize: "15px",
  lineHeight: "28px",
  letterSpacing: "0px",
};

const FaqDescriptionSx = {
  fontFamily: "DM Sans",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "20px",
  letterSpacing: "0%",
  color: "rgba(111, 108, 144, 1)",
};

const TableHeaderSx = {
  textAlign: "center",
  fontFamily: "Poppins",
  fontWeight: 500,
  fontSize: "14px",
  lineHeight: "110%",
  letterSpacing: "0%",
  color: "rgba(18, 18, 18, 1)",
  padding: 1.5,
};

const UsageIdSx = {
  fontFamily: "Poppins",
  fontWeight: 500,
  fontSize: "14px",
  lineHeight: "110%",
  letterSpacing: "0%",
  color: "rgba(33, 39, 42, 1)",
  textAlign: "center",
};

const DateSx = {
  fontFamily: "Poppins",
  fontWeight: 500,
  fontSize: "14px",
  lineHeight: "110%",
  letterSpacing: "0%",
  color: "rgba(101, 101, 101, 1)",
  textAlign: "center",
};

const BackButtonSx = {
  minWidth: "auto",
  padding: "4px 0px",
  color: "rgba(107, 142, 118, 1)",
  fontFamily: "Poppins",
  fontWeight: 500,
  fontSize: "16px",
  textTransform: "none",
  "&:hover": { backgroundColor: "transparent" },
  "& .MuiButton-startIcon": { color: "rgba(107, 142, 118, 1)" },
};

const faqData = [
  {
    question: "What is My- Wallet?",
    answer:
      "My- Wallet is a secure and convenient digital wallet that allows you to store and manage your cards, make payments, and track your spending all from your phone. It's designed to make transactions faster and safer.",
  },
  {
    question: "How do I add my credit or debit card to the wallet?",
    answer:
      "To add a card, simply open the wallet app, go to the 'Add Card' section, and enter your card details. You can also scan your card using your phone’s camera for faster entry.",
  },
  {
    question: "Is my information secure in My- Wallet?",
    answer:
      "Yes, we take your security seriously. Your data is protected with end-to-end encryption, and we use two-factor authentication (2FA) to add an extra layer of protection to your account.",
  },
  {
    question: "Can I use My- Wallet for international payments?",
    answer:
      "Yes, My- Wallet supports international payments. You can easily make payments in different currencies wherever the service is accepted, and our exchange rates are competitive.",
  },
  {
    question: "What if I lose my phone or it gets stolen?",
    answer:
      "If you lose your phone or suspect it has been stolen, you can remotely lock or disable your wallet account via the 'Find My Wallet' feature in the app. You can also contact our support team immediately to secure your account.",
  },
  {
    question: "How do I transfer money using My- Wallet?",
    answer:
      "To transfer money, go to the 'Send Money' section, select the recipient from your contacts or enter their details manually. Choose the amount and payment method, then confirm the transfer. Transfers are processed instantly or within a few minutes depending on the payment method.",
  },
  {
    question: "Are there any fees for using My- Wallet?",
    answer:
      "Basic transactions such as adding funds or transferring money to a bank account are free. However, certain premium features (like international transfers or instant transfers) may have a small fee associated with them. You can view the full fee schedule in the app’s 'Pricing' section.",
  },
  {
    question: "Can I withdraw money from my wallet to my bank account?",
    answer:
      "Yes, you can easily withdraw funds to your linked bank account. Go to the 'Withdraw' section, enter the amount you want to transfer, and select your bank account. Withdrawals typically take [X] business days to process.",
  },
];

const getStatusChip = (statusRaw) => {
  const status = statusRaw?.trim().toLowerCase(); // Normalize

  let bgColor = "transparent";
  let textColor = "black";
  let dotColor = "gray";
  let label = statusRaw; // original label for display

  switch (status) {
    case "paid":
      bgColor = "rgba(165, 255, 150, 0.28)"; // brighter green
      textColor = "rgba(30, 150, 9, 1)";
      dotColor = "rgba(30, 150, 9, 1)";
      break;
    case "cancelled":
    case "failed":
      bgColor = "rgba(167, 9, 9, 0.05)";
      textColor = "rgba(167, 9, 9, 1)";
      dotColor = "rgba(167, 9, 9, 1)";
      break;
    default:
      bgColor = "rgba(220, 220, 220, 0.3)";
      textColor = "gray";
      dotColor = "gray";
  }

  return (
    <Chip
      icon={
        <FiberManualRecordIcon
          style={{
            fontSize: "10px",
            color: dotColor,
            marginLeft: "5px",
            marginRight: "-4px",
          }}
        />
      }
      label={label}
      size="small"
      sx={{
        backgroundColor: bgColor,
        color: textColor,
        fontWeight: 500,
        fontSize: "13px",
        fontFamily: "Poppins",
        borderRadius: "15px",
        pl: 1,
        pr: 1.5,
      }}
    />
  );
};

const MyWallet = () => {
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const [coinUsageData, setCoinUsageData] = useState([]);
  const { walletBalance, setWalletBalance } = useContext(GlobalContext);

  const navigate = useNavigate();

  const [openModal, setOpenModal] = useState(false);

  const handleOpen = () => setOpenModal(true);
  const handleClose = () => setOpenModal(false);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  // Fetch wallet transactions
  useEffect(() => {
    let isMounted = true;

    const fetchTransactions = async () => {
      try {
        const res = await axiosMain.get("/api/user/wallet/transactions");
        if (isMounted && res.data?.status) {
          setCoinUsageData(res?.data?.data || []);
          console.log("Wallet Transactions:", res.data.data);
        }
      } catch (error) {
        console.error("Error fetching wallet transactions:", error);
      }
    };

    fetchTransactions();
    return () => {
      isMounted = false;
    };
  }, []);

  // Helper to abbreviate numbers (e.g., 1000 -> 1k, 1000000 -> 1m)
  const abbreviateNumber = (num) => {
    const safeNum = Math.abs(Number(num) || 0);
    if (safeNum >= 1e12) {
      return (safeNum / 1e12).toFixed(1).replace(/\.0$/, '') + 'T';
    }
    if (safeNum >= 1e9) {
      return (safeNum / 1e9).toFixed(1).replace(/\.0$/, '') + 'B';
    }
    if (safeNum >= 1e6) {
      return (safeNum / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
    }
    if (safeNum >= 1e3) {
      return (safeNum / 1e3).toFixed(1).replace(/\.0$/, '') + 'K';
    }
    return safeNum.toString();
  };

  // Helper for signed abbreviated display (for table)
  const formatSignedAmount = (amount, isCredit) => {
    const absFormatted = abbreviateNumber(amount);
    return `${isCredit ? '+' : '-'}${absFormatted}`;
  };

  return (
    <Box p={2}>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Button
          sx={BackButtonSx}
          variant="text"
          onClick={handleBack}
          startIcon={<ChevronLeftIcon />}
        >
          Back
        </Button>
      </Box>

      <Typography sx={HeadTextSx} className="mb-2">
        My Wallet
      </Typography>

      <Grid container spacing={2}>
        {/* Left Column */}
        <Grid item xs={12} md={4}>
          <Box display="flex" flexDirection="column" height="100%">
            {/* Wallet Card */}
            <Card
              variant="outlined"
              sx={{ borderRadius: 3, borderColor: "#E0E0E0", mb: 2 }}
            >
              <CardContent sx={{ px: 3, py: 2 }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    <Typography sx={HeadTextSx}>Wallet Balance</Typography>
                    <Typography sx={TitleTextSx}>
                      {abbreviateNumber(walletBalance)} Coins
                    </Typography>
                  </Box>
                  <div>
                    <Button
                      variant="contained"
                      sx={{
                        background: "rgba(107, 142, 118, 1)",
                        color: "white",
                        textTransform: "none",
                        fontSize: "13px",
                        px: 2,
                        py: 0.5,
                        borderRadius: 2,
                        boxShadow: "none",
                      }}
                      onClick={handleOpen}
                    >
                      Add Coins
                    </Button>

                    {/* Modal */}
                    <AddCoinsModal open={openModal} onClose={handleClose} />
                  </div>
                </Box>
              </CardContent>
            </Card>

            {/* FAQ Card */}
            <Card
              variant="outlined"
              sx={{ borderRadius: 3, borderColor: "#E0E0E0", flexGrow: 1 }}
            >
              <CardContent sx={{ px: 3, py: 2 }}>
                <Typography sx={HeadTextSx}>FAQs</Typography>
                {faqData.map((faq, index) => (
                  <Accordion
                    className="text-start"
                    key={index}
                    expanded={expandedFAQ === index}
                    onChange={() =>
                      setExpandedFAQ(expandedFAQ === index ? null : index)
                    }
                    elevation={0}
                    disableGutters
                    sx={{
                      borderBottom: "1px solid #e0e0e0",
                      "&:before": { display: "none" },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      sx={{
                        paddingX: 0,
                        minHeight: 40,
                        "& .MuiAccordionSummary-content": { marginY: "8px" },
                      }}
                    >
                      <Typography sx={FaqTitleSx}>{faq.question}</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pl: 0, pb: 2 }}>
                      <Typography sx={FaqDescriptionSx}>
                        {faq.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </CardContent>
            </Card>
          </Box>
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} md={8}>
          <Box
            sx={{
              border: "1px solid rgba(221, 221, 221, 1)",
              borderRadius: "8px",
            }}
          >
            <CardContent>
              <Typography sx={HeadTextSx} gutterBottom className="mb-2">
                Coin Usage
              </Typography>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow
                      sx={{ backgroundColor: "rgba(240, 240, 240, 1)" }}
                    >
                      <TableCell sx={TableHeaderSx}>Usage ID ↑</TableCell>
                      <TableCell sx={TableHeaderSx}>Date ↑</TableCell>
                      <TableCell sx={TableHeaderSx}>Coins Used ↑</TableCell>
                      <TableCell sx={TableHeaderSx}>Used For Campign</TableCell>
                      <TableCell sx={TableHeaderSx}>Status ↑</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {coinUsageData?.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          No transaction history found.
                        </TableCell>
                      </TableRow>
                    ) : (
                      coinUsageData.map((row) => {
                        const isCredit = row.type === "credit";
                        const formattedDate = new Date(
                          row.created_at
                        ).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        });

                        // Split description
                        let tooltipText = "-";
                        if (row.brand_name && row.campaign_name) {
                          tooltipText = `${row.brand_name} (${row.campaign_name})`;
                        } else if (row.campaign_name) {
                          tooltipText = row.campaign_name;
                        } else {
                          // fallback to parsed description
                          const descriptionParts = row.description?.split(":");
                          tooltipText =
                            descriptionParts?.length > 1
                              ? descriptionParts.slice(1).join(":").trim()
                              : row.description || "-";
                        }

                        return (
                          <TableRow key={row.id}>
                            <TableCell sx={UsageIdSx}>{row.id}</TableCell>
                            <TableCell sx={DateSx}>{formattedDate}</TableCell>
                            <TableCell sx={DateSx}>
                              {formatSignedAmount(row.amount, isCredit)}
                            </TableCell>

                            {/* Used For column */}
                            <TableCell
                              sx={{ maxWidth: 200, textAlign: "center" }}
                            >
                              <Tooltip
                                title={
                                  <Box
                                    sx={{
                                      padding: "4px 8px",
                                      borderRadius: "16px",
                                      fontSize: "13px",
                                      fontFamily: "Poppins",
                                      color: "white",
                                    }}
                                  >
                                    {tooltipText}
                                  </Box>
                                }
                                placement="top"
                                arrow
                              >
                                <Typography
                                  sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    display: "block",
                                    fontSize: "14px",
                                    fontFamily: "Poppins",
                                    cursor: "default",
                                  }}
                                >
                                  {tooltipText?.length > 20
                                    ? `${tooltipText.slice(0, 20)}...`
                                    : tooltipText || "-"}
                                </Typography>
                              </Tooltip>
                            </TableCell>

                            <TableCell className="text-center">
                              {getStatusChip(row.status)}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MyWallet;